/**
 * ShareLinkSection.tsx
 * 
 * This component handles the display and copy functionality for the share link 
 * of a posit. It includes a disabled text input showing the full URL and a 
 * copy button that copies the URL to the clipboard.
 * 
 * Changes: 
 * - Extracted from ShareDialog.tsx to improve component modularity
 * - Added analytics tracking for copy events
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from "sonner";
import { Copy, Check } from 'lucide-react';
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

interface ShareLinkSectionProps {
  shareUrl: string;
  isPremium?: boolean;
}

export function ShareLinkSection({ shareUrl, isPremium = false }: ShareLinkSectionProps) {
  const [copiedShared, setCopiedShared] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedShared(true);
      
      // Track analytics event
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        status: 'success',
        share_type: 'link',
        is_premium: isPremium
      });
      
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedShared(false), 2000);
    } catch (err) {
      
      // Track analytics event for error
      trackEvent(ANALYTICS_EVENTS.SHARE_POSIT, {
        status: 'error',
        share_type: 'link',
        is_premium: isPremium
      });
      
      toast.error("Failed to copy link");
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <Input
        className="text-sm"
        value={shareUrl}
        readOnly
      />
      <Button
        variant="outline"
        size="icon"
        onClick={handleCopyLink}
        className={`shrink-0 transition-all ${copiedShared ? 'bg-green-500/10 border-green-500/20 text-green-600' : ''}`}
      >
        {copiedShared ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
} 